import * as React from "react"
import { useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/csrf"

/**
 * Starts Google sign-in. OmniAuth needs a real form POST (not an Inertia
 * request) because it redirects to Google. The CSRF token and timezone are
 * filled in on submit so server rendering stays deterministic.
 */
export function GoogleButton({
  label,
  fields = {},
}: {
  /** Defaults to "Continue with Google". */
  label?: string
  /** Extra values passed through the Google round trip, e.g. the program and access code being signed up for. */
  fields?: Record<string, string>
}) {
  const intl = useIntl()
  const tokenRef = React.useRef<HTMLInputElement>(null)
  const timezoneRef = React.useRef<HTMLInputElement>(null)

  return (
    <form
      method="post"
      action="/auth/google_oauth2"
      onSubmit={() => {
        if (tokenRef.current) tokenRef.current.value = csrfToken()
        if (timezoneRef.current) timezoneRef.current.value = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ""
      }}
    >
      <input ref={tokenRef} type="hidden" name="authenticity_token" defaultValue="" />
      <input ref={timezoneRef} type="hidden" name="timezone" defaultValue="" />
      {Object.entries(fields).map(([name, value]) => (
        <input key={name} type="hidden" name={name} value={value} readOnly />
      ))}
      <Button type="submit" variant="secondary" className="w-full">
        <GoogleMark /> {label ?? intl.formatMessage({ id: "auth.continue_with_google" })}
      </Button>
    </form>
  )
}

function GoogleMark() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  )
}

/** "or" divider between Google sign-in and the email form. */
export function OrDivider() {
  const intl = useIntl()
  return (
    <div className="my-6 flex items-center gap-3" role="separator">
      <span className="h-px flex-1 bg-hairline" />
      <span className="text-xs uppercase tracking-wider text-ink-muted">{intl.formatMessage({ id: "auth.or" })}</span>
      <span className="h-px flex-1 bg-hairline" />
    </div>
  )
}
